import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/services/api';
import { onBillingUsageChanged, onPlanChanged } from '@/lib/billingEvents';
import { canProcessPendingApplications } from '@/lib/pendingProcessing';
import { patchUnlockedDeferredApplications } from '@/lib/recruiterCache';
import { usePendingBillingStore } from '@/stores/pendingBillingStore';
import { useBillingStatus } from '@/hooks/useBillingStatus';
/**
 * Syncs deferred-application counts from the BE on every page.
 * Banner totals use API truth only — not the partial applications-list cache
 * (which differs per browser/tab and caused 53 vs hidden banner).
 */
export function useSyncPendingBillingCount() {
  const { selectedCompany } = useAuth();
  const queryClient = useQueryClient();
  const setDeferredCounts = usePendingBillingStore(s => s.setDeferredCounts);
  const { data: billingStatus } = useBillingStatus();
  const hasProcessingCredits = canProcessPendingApplications(billingStatus);
  const companyId = selectedCompany?.id;

  const applyCounts = useCallback(
    (counts: { pendingUpgrade: number; readyToProcess: number; billingRetry: number }) => {
      if (!companyId) return;
      setDeferredCounts(counts);
    },
    [companyId, setDeferredCounts],
  );

  const sync = useCallback(async () => {
    if (!companyId) {
      setDeferredCounts({ pendingUpgrade: 0, readyToProcess: 0, billingRetry: 0 });
      return;
    }
    try {
      const result = await api.applications.unlockDeferred(companyId);
      if (result.unlocked > 0) {
        patchUnlockedDeferredApplications(queryClient, companyId);
      }
      applyCounts({
        pendingUpgrade: result.pendingUpgrade,
        readyToProcess: result.readyToProcess,
        billingRetry: result.billingRetry,
      });
    } catch {
      // Keep the last known counts on transient fetch errors.
    }
  }, [companyId, setDeferredCounts, queryClient, applyCounts]);

  const syncRef = useRef(sync);
  syncRef.current = sync;

  useEffect(() => {
    void syncRef.current();
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    const onFocus = () => {
      void syncRef.current();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [companyId]);

  useEffect(() => {
    return onPlanChanged(() => {
      void syncRef.current();
    });
  }, [companyId]);

  useEffect(() => {
    return onBillingUsageChanged(() => {
      void syncRef.current();
    });
  }, [companyId]);

}
